import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Check, FileDown, Landmark, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { fmtIsk, fmtUsdApprox, ISK_PER_USD } from "@/lib/price";

const fmtD = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/invoice/${token}`)
      .then((res) => setInv(res.data))
      .catch(() => setFailed(true));
  }, [token]);

  if (failed) {
    return (
      <div data-testid="invoice-not-found" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">This invoice link has gone cold.</p>
        <p className="mt-3 max-w-sm text-sm text-neutral-500">Check the link from your email, or contact your concierge.</p>
        <Link to="/" data-testid="invoice-notfound-home" className="mt-8 border border-amber-500/60 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black">
          Exclusive Reykjavik
        </Link>
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070709]">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }

  const t = inv.totals;

  return (
    <div data-testid="invoice-view" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-16">
        <div className="text-center">
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="mx-auto h-16 w-auto mix-blend-screen" />
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.4em] text-amber-300/80">Exclusive Reykjavik</p>
          <h1 className="mt-6 font-serif text-4xl font-light tracking-wide text-neutral-50">{inv.doc_type === "credit_note" ? "Credit Note" : "Invoice"}</h1>
          <p data-testid="invoice-view-number" className="mt-2 text-sm tracking-[0.2em] text-amber-300/90">{inv.invoice_number}</p>
          {inv.doc_type === "credit_note" && inv.credit_for && (
            <p data-testid="invoice-view-credit-for" className="mt-1 text-[10px] uppercase tracking-[0.25em] text-neutral-500">Credit note for invoice {inv.credit_for}</p>
          )}
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500">
            issued {fmtD(inv.issue_date)}
            {inv.due_date ? ` · due ${fmtD(inv.due_date)}` : ""}
          </p>
          {inv.status === "paid" && (
            <p data-testid="invoice-view-paid" className="mx-auto mt-4 flex w-fit items-center gap-2 border border-emerald-400/40 bg-emerald-500/10 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-emerald-200">
              <Check className="h-3.5 w-3.5" /> Paid — thank you
            </p>
          )}
        </div>

        <div className="mt-10 border border-amber-500/20 bg-[#0c0c10] p-6 sm:p-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Billed to</p>
          <p data-testid="invoice-view-client" className="mt-2 font-serif text-xl font-light text-neutral-50">{inv.client_name}</p>

          <div className="mt-6 divide-y divide-neutral-800/70 border-t border-amber-500/10">
            {inv.lines.map((l, i) => (
              <div key={i} data-testid={`invoice-view-line-${i}`} className="flex items-baseline justify-between gap-4 py-3">
                <div>
                  <p className="text-sm text-neutral-100">{l.description}</p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                    {Number(l.qty) !== 1 ? `${l.qty} × ${fmtIsk(l.unit_price_isk)} · ` : ""}VSK {l.vat_rate ? `${l.vat_rate}% included` : "free"}
                  </p>
                </div>
                <span className="font-luxe text-base tracking-wide text-amber-100">{fmtIsk(Math.round(l.qty * l.unit_price_isk))}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-1.5 border-t border-amber-500/10 pt-4 text-right">
            <p className="text-sm text-neutral-400">
              Subtotal (VAT included) <span data-testid="invoice-view-subtotal" className="ml-4 font-luxe tracking-wide text-amber-100">{fmtIsk(t.subtotal_isk)}</span>
            </p>
            {Object.entries(t.vat_by_rate).map(([rate, amount]) => (
              <p key={rate} className="text-xs text-neutral-500">
                Of which VSK {rate}% <span className="ml-4">{fmtIsk(amount)}</span>
              </p>
            ))}
            <p className="text-sm text-neutral-400">
              Total without VAT <span data-testid="invoice-view-net" className="ml-4 font-luxe tracking-wide text-neutral-200">{fmtIsk(t.net_isk)}</span>
            </p>
            {t.deposit_isk !== 0 && (
              <p className="text-sm text-neutral-400">
                {t.deposit_isk > 0 ? "Deposit already paid" : "Deposit to refund"}{" "}
                <span className="ml-4 font-luxe tracking-wide text-emerald-200">− {fmtIsk(Math.abs(t.deposit_isk))}</span>
              </p>
            )}
            <p className="pt-1 text-base font-semibold text-neutral-100">
              Balance due{" "}
              <span data-testid="invoice-view-balance" className="ml-4 font-luxe text-2xl tracking-wide text-amber-100">
                {fmtIsk(t.balance_isk)} <span className="text-sm font-normal text-neutral-500">{fmtUsdApprox(t.balance_isk / ISK_PER_USD)}</span>
              </span>
            </p>
          </div>

          {inv.payment_details && (
            <div data-testid="invoice-view-payment" className="mt-6 border border-amber-500/30 bg-amber-500/5 p-4">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/90">
                <Landmark className="h-3.5 w-3.5" /> Payment by transfer
              </p>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-neutral-200">{inv.payment_details}</p>
            </div>
          )}

          {inv.notes && <p className="mt-5 whitespace-pre-line font-serif text-sm italic leading-relaxed text-neutral-400">{inv.notes}</p>}
        </div>

        <div className="mt-6 text-center">
          <a
            data-testid="invoice-view-pdf"
            href={`${API}/public/invoice/${token}/pdf`}
            className="inline-flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
          >
            <FileDown className="h-4 w-4" /> Download PDF
          </a>
        </div>

        <p className="mt-10 text-center font-serif text-sm italic text-neutral-500">
          Exclusive Reykjavik — Mecca of Happiness slf, kt 690715-1390, VAT 141825 — viking@exclusivereykjavik.is — +354 615 0055
        </p>
      </div>
    </div>
  );
}
